import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import LegalEntity from "legal-entities/legal-entity";
import { administratorInvitationApi } from "../_api/administrator-invitation-api";
import { AdministratorInvitationJSON } from "../_schema/administrator-invitation-schema";

type AdminInvitationStoreState = {
  byEntityId: Record<string, AdministratorInvitationJSON[]>
  byId: Record<string, AdministratorInvitationJSON>;
};

const initialState: AdminInvitationStoreState = {
  byEntityId: {},
  byId: {}
};

const administratorInvitationsSlice = createSlice({
  name: "administratorInvitations",
  initialState,
  reducers: {
    addEntityAdministratorInvitation: (state, action: PayloadAction<AdministratorInvitationJSON>) => {
      if (!action.payload.id) return;
      state.byId[action.payload.id] = action.payload;

      const entityId = action.payload.entityId;
      if (!entityId) return;
      state.byEntityId[entityId] =
        state.byEntityId[entityId]?.filter(
          (existingInvitation) => existingInvitation.id !== action.payload.id
        ) ?? [];
      state.byEntityId[entityId].push(action.payload);
    },
    removeEntityAdministratorInvitation: (state, action: PayloadAction<AdministratorInvitationJSON>) => {
      if (!action.payload.id) return;
      delete state.byId[action.payload.id];

      const entityId = action.payload.entityId;
      if (!entityId) return;
      state.byEntityId[entityId] =
        state.byEntityId[entityId]?.filter(
          (existingInvitation) => existingInvitation.id !== action.payload.id
        ) ?? [];
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(administratorInvitationApi.endpoints.getAdministratorInvitationById.matchFulfilled, (state, action) => {
      const invite = action.payload;
      if (!invite.id) return;
      state.byId[invite.id] = action.payload;
      
      const entityId = invite.entityId;
      if (!entityId) return;
      state.byEntityId[entityId] =
        state.byEntityId[entityId]?.filter(
          (existingInvitation) => existingInvitation.id !== invite.id
        ) ?? [];
      state.byEntityId[entityId].push(invite);
    });
    builder.addMatcher(administratorInvitationApi.endpoints.getAdminInvitationsForLegalEntity.matchFulfilled, (state, action) => {
      const entityId = action.meta.arg.originalArgs?.value;
      const adminInvites = action.payload
      if (!entityId || !adminInvites) return;
      state.byEntityId[entityId] = adminInvites;

      adminInvites.forEach((invitation) => {
        if (!invitation.id) return;
        state.byId[invitation.id] = invitation;
      });
    });
  },
});

export const { addEntityAdministratorInvitation, removeEntityAdministratorInvitation } =
  administratorInvitationsSlice.actions;

export const getAdminInvitationsForLegalEntity = (entityId: Guid) => (state: RootState) =>
  state.administratorInvitations.byEntityId[entityId.value];

export const getAdminInvitationById = (id: Guid) => (state: RootState) =>
  state.administratorInvitations.byId[id.value];

export const getEntityAgreementById = (id: Guid) => (state: RootState) =>
  state.legalEntityAgreements.byId.entries[id.value];
export const getIsLoadingEntityAgreementById = (id: Guid) => (state: RootState) =>
  state.legalEntityAgreements.byId.loading[id.value];
export const getErrorLoadingEntityAgreementById = (id: Guid) => (state: RootState) =>
  state.legalEntityAgreements.byId.error[id.value];

export const getEntityAgreementsForLegalEntity = (entity: LegalEntity) => (state: RootState) =>
  state.legalEntityAgreements.byId.entries[entity.id?.value];
export const getIsLoadingEntityAgreementsForLegalEntity = (entity: LegalEntity) => (state: RootState) =>
  state.legalEntityAgreements.byId.loading[entity.id?.value];
export const getErrorLoadingAEntityAgreementsForLegalEntity = (entity: LegalEntity) => (state: RootState) =>
  state.legalEntityAgreements.byId.error[entity.id?.value];

export default administratorInvitationsSlice;
